import { supabase } from '../../lib/supabase';
import type { Database } from '../../types/database.types';

type TeamMember = Database['public']['Tables']['team_members']['Row'];
type TeamMemberInsert = Database['public']['Tables']['team_members']['Insert'];
type TeamMemberUpdate = Database['public']['Tables']['team_members']['Update'];


/**
 * API service for Team Member CRUD operations
 */
export const teamMembersApi = {
    /**
     * Get all team members for the current user
     */
    async getAll(): Promise<{ data: TeamMember[] | null; error: Error | null }> {
        try {
            const { data, error } = await supabase
                .from('team_members')
                .select('*')
                .order('name', { ascending: true });

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            return {
                data: null,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },

    /**
     * Get a single team member by ID
     */
    async getById(
        id: number
    ): Promise<{ data: TeamMember | null; error: Error | null }> {
        try {
            const { data, error } = await supabase
                .from('team_members')
                .select('*')
                .eq('id', id)
                .single();

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            return {
                data: null,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },

    /**
     * Create a new team member
     */
    async create(
        teamMember: TeamMemberInsert
    ): Promise<{ data: TeamMember | null; error: Error | null }> {
        try {
            const { data, error } = await supabase
                .from('team_members')
                .insert(teamMember)
                .select()
                .single();

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            return {
                data: null,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },

    /**
     * Update an existing team member
     */
    async update(
        id: number,
        updates: TeamMemberUpdate
    ): Promise<{ data: TeamMember | null; error: Error | null }> {
        try {
            const { data, error } = await supabase
                .from('team_members')
                .update(updates)
                .eq('id', id)
                .select()
                .single();

            if (error) throw error;
            return { data, error: null };
        } catch (error) {
            return {
                data: null,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },

    /**
     * Delete a team member
     */
    async delete(id: number): Promise<{ error: Error | null }> {
        try {
            const { error } = await supabase.from('team_members').delete().eq('id', id);

            if (error) throw error;
            return { error: null };
        } catch (error) {
            return {
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },

    /**
     * Get count of tasks assigned to a team member
     */
    async getAssignedTasksCount(
        memberId: number
    ): Promise<{ data: number | null; error: Error | null }> {
        try {
            const { count, error } = await supabase
                .from('tasks')
                .select('*', { count: 'exact', head: true })
                .eq('assigned_member_id', memberId);

            if (error) throw error;
            return { data: count, error: null };
        } catch (error) {
            return {
                data: null,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    },
};

